/**
 * ノード型 1 つから「開ける詳細画面」を決める（大枠設計 §7 の段階的探索の行き先）。
 *
 * 逆引きの一覧は画面ごとに違うが、そこに並ぶのは同じ語彙のノードである。どの型が
 * どの詳細へ飛ぶかを一覧ごとに書くと、詳細ルートが増えたときに「検索からは飛べるが
 * 用語詳細の逆引きからは飛べない」という説明の付かない差が生まれる。判断をこの 1 関数に
 * 閉じて、一覧側は行き先を知らずに済むようにする。
 *
 * コンポーネント（Component）は**単体の詳細ルートを持たないが、所属画面へ送る**。コンポーネントは所属画面の
 * コンポーネント表としてのみ意味を持ち、単体の画面にすると「画面から切り離されたコンポーネント」という実体の無い
 * 単位を UI が作ってしまう。いっぽう行き先を持たせないと、PJ 差分の一覧に並ぶコンポーネントの行が
 * **1 件残らず行き止まりになる**。PJ がコンポーネントしか触っていない PJ では、差分一覧から開ける先が
 * 1 つも無くなる。コンポーネント表は所属画面が持っているので、そこへ送ればコンポーネントの中身は読める。
 * コンポーネント ID は `<画面>/<コンポーネント>` なので、最初の区切りまでが所属画面である。
 *
 * 操作（Operation）も現時点では詳細ルートを持たない。操作の中身は業務詳細が所属操作の
 * 並びとして出す（決定 ap）。操作単体の permalink は次段の課題であり、足すときはこの
 * 1 関数へ `operation` を足せば全一覧の行き先が同時に変わる。
 *
 * 業務ルール（Rule）は業務から独立した画面（`/rules/$ruleId`）へ送る。PJ の差分の業務ルールの
 * 「詳細で見る」もここを通る。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 仕様グラフのノード型から、開ける詳細画面の種別を決める。
 * @graph-connects none
 */
import { MASTER_DETAIL_TYPES, type MasterDetailType } from '@cortex/domain-spec-console';

import { SPEC_NODE_TYPE_VALUES, type SpecNodeTypeValue } from './spec-node-types.js';

/** ノード 1 件の行き先。`none` は詳細ルートを持たない型。 */
export type SpecNodeLinkTarget =
  | { kind: 'user-story'; usId: string }
  | { kind: 'business'; businessId: string }
  | { kind: 'rule'; ruleId: string }
  | { kind: 'screen'; screenId: string }
  | { kind: 'term'; termId: string }
  | { kind: 'master'; masterType: MasterDetailType; masterId: string }
  | { kind: 'none' };

/**
 * ノード型と ID から詳細画面の行き先を決める。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ノード型と ID から、開ける詳細画面と渡すパラメータを決める。
 * @graph-connects none
 */
export function resolveSpecNodeLink(
  nodeType: SpecNodeTypeValue,
  nodeId: string,
): SpecNodeLinkTarget {
  if (nodeType === 'Flow') return { kind: 'user-story', usId: nodeId };
  if (nodeType === 'Business') return { kind: 'business', businessId: nodeId };
  if (nodeType === 'Rule') return { kind: 'rule', ruleId: nodeId };
  if (nodeType === 'Screen') return { kind: 'screen', screenId: nodeId };
  if (nodeType === 'Component') return screenOfComponent(nodeId);
  if (nodeType === 'Term') return { kind: 'term', termId: nodeId };
  if (nodeType === 'Actor') return { kind: 'master', masterType: 'actor', masterId: nodeId };
  if (nodeType === 'Domain') return { kind: 'master', masterType: 'domain', masterId: nodeId };
  if (nodeType === 'Stage') return { kind: 'master', masterType: 'stage', masterId: nodeId };
  return { kind: 'none' };
}

/**
 * コンポーネントの ID から所属画面の行き先を決める。
 *
 * 区切りを持たない ID は所属画面が読めないので行き先を持たせない。画面 ID を推測して外すと、
 * 利用者は無関係な画面へ飛ばされ、そこにコンポーネントが無いことを「壊れている」と読む。
 *
 * @param nodeId コンポーネントの ID（`<画面>/<コンポーネント>`）
 * @returns 所属画面の行き先。読めなければ行き先なし
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business コンポーネントの ID から所属画面の行き先を決める。
 * @graph-connects none
 */
function screenOfComponent(nodeId: string): SpecNodeLinkTarget {
  const separator = nodeId.indexOf('/');
  if (separator <= 0) return { kind: 'none' };
  return { kind: 'screen', screenId: nodeId.slice(0, separator) };
}

/**
 * パスパラメータの文字列が分類マスターの型かを判定する。
 *
 * `/masters/$type/$id` は手打ちの URL や古いブックマークからも到達する。型名を検証せずに
 * 取得へ渡すと、BFF の入力検証エラー（`BAD_REQUEST`）が通信障害と同じ見え方になり、
 * 利用者には「壊れている」としか映らない。実在しない型は未存在として扱う。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business パスパラメータが分類マスターの型かを判定する。
 * @graph-connects none
 */
export function isMasterDetailType(value: string): value is MasterDetailType {
  return (MASTER_DETAIL_TYPES as readonly string[]).includes(value);
}

/**
 * 変更ログ 1 行の対象から、開ける詳細画面のノード型を決める。
 *
 * 変更ログの行は対象の種別を `node` / `edge` の 2 値でしか持たず、ノード型
 * （Business / Term など）は列として持たない。列を足すと、同じ事実が属性 JSON と列の 2 表現に
 * なる（制約 11）。したがってノード型は属性 JSON の `nodeType` からしか読めない。
 *
 * 読めない行はリンクにしない。ノード型を推測して外すと、利用者は無関係な詳細画面へ飛ばされ、
 * 「変更ログが壊れている」と読む。エッジと削除済みの行も同じ理由でリンクにしない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 変更ログ 1 行の属性 JSON から、開ける詳細画面のノード型を決める。
 * @graph-connects none
 */
export function resolveChangeTargetNodeType(
  targetKind: string,
  beforeValue: Record<string, unknown> | null,
  afterValue: Record<string, unknown> | null,
): SpecNodeTypeValue | null {
  if (targetKind !== 'node' || afterValue === null) return null;
  const declared = afterValue.nodeType ?? beforeValue?.nodeType;
  if (typeof declared !== 'string' || !isSpecNodeType(declared)) return null;
  return declared;
}

/**
 * 文字列が仕様グラフのノード型かを判定する。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 文字列が仕様グラフのノード型かを判定する。
 * @graph-connects none
 */
function isSpecNodeType(value: string): value is SpecNodeTypeValue {
  return (SPEC_NODE_TYPE_VALUES as readonly string[]).includes(value);
}
